import { styled } from "goober";
import { useMemo } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { ExecutionOutputView } from "../../tools";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { deriveChatOutputs, iconForFileName } from "./chatOutputs";

const SwitcherOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 9999px;
  background: var(--alpha4);
  cursor: pointer;
  &:hover {
    background: var(--alpha8);
  }
`;

const OpenerDivider = styled("div")`
  width: 0.5px;
  height: 100%;
  background: var(--border-alpha);
  flex-shrink: 0;
`;

type Props = {
  entries: readonly ChatEntryWithSubTables[];
  currentExecutionId: string;
  currentFileName: string;
  onShowOutput: (output: ExecutionOutputView) => void;
};

export function OutputSwitcher({
  entries,
  currentExecutionId,
  currentFileName,
  onShowOutput,
}: Props) {
  const outputs = useMemo(() => deriveChatOutputs(entries), [entries]);

  return (
    <Dropdown
      align="bottom-left"
      margin={6}
      minWidth={220}
      opener={
        <SwitcherOpener aria-label="Switch output">
          <Text
            variant="text-small-md"
            color="var(--text-primary)"
            style={{ padding: "0 2px" }}
          >
            {outputs.length}
          </Text>
          <OpenerDivider />
          <span style={{ display: "flex" }}>
            <Icon name={iconForFileName(currentFileName)} size={12} />
          </span>
          <Text variant="text-small-md" ellipsis>
            {currentFileName}
          </Text>
          <Icon name="chevron-down" size={12} />
        </SwitcherOpener>
      }
      sections={[
        {
          label: "Outputs",
          items: outputs.map((o) => ({
            type: "button" as const,
            key: o.executionId,
            left: <Icon name={iconForFileName(o.fileName)} size={16} />,
            label: o.fileName,
            selected: o.executionId === currentExecutionId,
            right:
              o.executionId === currentExecutionId ? (
                <Icon name="check" size={16} />
              ) : undefined,
            onClick: () =>
              onShowOutput({
                executionId: o.executionId,
                fileName: o.fileName,
                mediaType: "",
                sizeBytes: 0,
              }),
          })),
        },
      ]}
    />
  );
}
